import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { reaction, toJS } from 'mobx';
import { FormattedMessage, useIntl } from 'react-intl';
import dayJS from 'dayjs';

import {
    CalculatorStepAccordion,
    MessagePropType,
    Paragraph,
    Question,
    CircleSpinner,
    MessageBanner,
    AnimatedFadeContainer,
    createOptionMessageResolver,
    Sizes,
    Colours,
    useForm,
    Locales,
    FormComputed,
    trackHeapEvent,
    useCalculator,
    BOOLEAN_OPTIONS,
} from '@ratehub/base-ui';
import {
    PAYMENT_FREQUENCIES,
    RATE_TYPES,
} from '@ratehub/documents/mtg-journey-document/stable';
import {
    RATE_TYPE_MESSAGES,
    RATE_TERM_MESSAGES,
    PAYMENT_FREQUENCY_MESSAGES,
    MORTGAGE_PLACEHOLDERS,
    getTermOptionsByRateType,
} from '@ratehub/mtg-common';

import PENALTY_PROVIDERS from '../definitions/PenaltyProviders';
import BreakagePenaltyComputed from './BreakagePenaltyComputed';
import CalculatorResultsBox from './CalculatorResultsBox';


function RefinanceStepPenaltyCalculation({
    title,
    ctaText,
    ctaDataName,
    stepNumber,

    onToggleExpand,
    onConfirmComplete,
    ...otherProps
}) {
    const form = useForm();
    const intl = useIntl();

    const calculatorState = useCalculator();
    const document = calculatorState.document;

    const {
        paymentFrequency,
        mortgagePayment,
        currentRate,
        knownPenaltyFee,
        existingProvider,
        currentRateType,
        originalTermLength,
        startDate,
    } = getSavedFormValues(toJS(document?.journey?.calculators));

    // REQUIREMENT: we want to track the # of times this happens (and look for patterns in values)
    // NOTE: spreading the values converts from getters to standard JS values.
    useEffect(() => reaction(
        () => form.values.calculatedPenaltyFeeError,
        error => {
            if (error) {
                trackHeapEvent('Refinance Calculator Failed Calculate Penalty', { ...form.values });
            }
        },
    ), [ form ]);

    return (
        <Container {...otherProps}>
            <BreakagePenaltyComputed
                name="calculatedPenaltyFee"
                getFormValues={getFormValuesForBreakagePenalty}
                isDetailed={false}
            />

            <FormComputed
                name="breakagePenalty"
                value={getBreakagePenalty}
            />

            <CalculatorStepAccordion
                title={title}
                value="breakagePenalty"
                valueFormat="CAD0"
                ctaText={ctaText}
                ctaDataName={ctaDataName}
                stepNumber={stepNumber}
                showBadge={true}
                canBeDisabled={true}
                hasCompleteButton={true}
                onConfirmComplete={(_, formValues) => onConfirmComplete?.({ stepNumber, formValues, document })}
                onToggleExpand={onToggleExpand}
            >
                <div className="stepContent">
                    <If condition={form.values.calculatedPenaltyFeeIsFetching}>
                        <CircleSpinner />
                    </If>

                    <MessageBanner
                        className="messageBanner"
                        variant="success"
                        iconKey="light-bulb"
                        message={(
                            <FormattedMessage
                                id="mtg-calculators.Refinance.PenaltyStep.tipNotificationMessage"
                                defaultMessage="If you refinance your mortgage before your term is up, you will have to pay a penalty or breakage fee. Lenders calculate this based on your current mortgage balance, interest rate and the time remaining in your term."
                            />
                        )}
                    />

                    <Question
                        field={{
                            name: 'paymentFrequency',
                            type: 'choice',
                            value: paymentFrequency ?? PAYMENT_FREQUENCIES.MONTHLY,
                            options: Object.values(PAYMENT_FREQUENCIES),
                        }}
                        labelMessage={(
                            <FormattedMessage
                                id="mtg-calculators.Refinance.paymentFrequencyLabel"
                                defaultMessage="How often do you make mortgage payments?"
                            />
                        )}
                        type="dropdown"
                        renderOptionLabel={createOptionMessageResolver(intl, Object.values(PAYMENT_FREQUENCY_MESSAGES), 'Mortgages.paymentFrequencies.')}
                        isSideBySide={true}
                    />

                    <Question
                        field={{
                            name: 'mortgagePayment',
                            type: 'integer',
                            value: mortgagePayment,
                        }}
                        labelMessage={(
                            <FormattedMessage
                                id="mtg-calculators.Refinance.mortgagePaymentLabel"
                                defaultMessage="How much is your current monthly mortgage payment?"
                            />
                        )}
                        placeholder={intl.formatMessage(MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER)}
                        type="currency"
                        isSideBySide={true}
                    />

                    <Question
                        field={{
                            name: 'currentRate',
                            type: 'float',
                            value: currentRate,
                            validators: [
                                {
                                    fn: (rate) => rate <= 20,
                                    message: (
                                        <FormattedMessage
                                            id="mtg-calculators.Refinance.PenaltyStep.currentRate.errorMessage"
                                            defaultMessage="Please enter a valid rate"
                                        />
                                    ),
                                },
                            ],
                        }}
                        labelMessage={(
                            <FormattedMessage
                                id="mtg-calculators.Refinance.PenaltyStep.currentRate"
                                defaultMessage="What is your existing mortgage rate?"
                            />
                        )}
                        placeholder={intl.formatMessage(MORTGAGE_PLACEHOLDERS.PERCENT_PLACEHOLDER)}
                        type="percent"
                        isSideBySide={true}
                    />

                    <Question
                        field={{
                            name: 'penaltyQuestion',
                            type: 'boolean',
                            value: !!knownPenaltyFee,
                            options: BOOLEAN_OPTIONS,
                        }}
                        labelMessage={(
                            <FormattedMessage
                                id="mtg-calculators.Refinance.penaltyQuestionLabel"
                                defaultMessage="Do you know the penalty to break your existing mortgage?"
                            />
                        )}
                        type="radio"
                        flowDirection="row"
                        isSideBySide={true}
                    />

                    <If condition={form.values?.penaltyQuestion}>
                        <Question
                            field={{
                                name: 'knownPenaltyFee',
                                type: 'integer',
                                required: () => form?.values?.penaltyQuestion === true,
                                value: knownPenaltyFee,
                            }}
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculator.Refinance.penaltyFeeLabel"
                                    defaultMessage="How much is the penalty?"
                                />
                            )}
                            placeholder={intl.formatMessage(MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER)}
                            type="currency"
                            isSideBySide={true}
                        />
                    </If>


                    <AnimatedFadeContainer isVisible={form.values?.penaltyQuestion === false}>
                        <div className="divider">
                            <Paragraph
                                message={(
                                    <FormattedMessage
                                        id="mtg-calculator.Refinance.dividerMessage"
                                        defaultMessage="Let's calculate your penalty"
                                    />
                                )}
                                weight="medium"
                                size="medium"
                                className="calculate-penalty"
                            />
                        </div>

                        <Question
                            field={{
                                name: 'existingProvider',
                                type: 'choice',
                                options: createProviderOptions(intl),
                                value: existingProvider,
                            }}
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculators.Refinance.existingProvider"
                                    defaultMessage="Who is your mortgage provider?"
                                />
                            )}
                            type="dropdown"
                            isSideBySide={true}
                        />

                        <Question
                            field={{
                                name: 'currentRateType',
                                type: 'choice',
                                options: Object.values(RATE_TYPES),
                                value: currentRateType,
                            }}
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculators.Refinance.PenaltyStep.currentRateType"
                                    defaultMessage="What type of rate is it?"
                                />
                            )}
                            renderOptionLabel={createOptionMessageResolver(intl, Object.values(RATE_TYPE_MESSAGES), 'Mortgages.rateTypes.')}
                            isSideBySide={true}
                        />

                        <Question
                            field={{
                                name: 'originalTermLength',
                                type: 'choice',
                                options: getTermOptionsByRateType(form.values.currentRateType ?? currentRateType),
                                value: originalTermLength,
                            }}
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculators.Refinance.originalTerm"
                                    defaultMessage="What is the original term of your mortgage?"
                                />
                            )}
                            type="dropdown"
                            renderOptionLabel={createYearOptionResolver(intl)}
                            isSideBySide={true}
                            tooltipMessage={(
                                <FormattedMessage
                                    id="mtg-calculators.Refinance.originalTerm.tooltip"
                                    defaultMessage="Your mortgage term is the length of time you committed to your mortgage rate. For example, on a 5-year fixed mortgage rate, the term is 5 years."
                                />
                            )}
                        />

                        <Question
                            field={{
                                name: 'startDate',
                                type: 'date',
                                value: startDate?.format('MM/DD/YYYY'),
                                validators: [
                                    {
                                        fn: (date, formValues) =>
                                            // Has the term already ended?
                                            dayJS().subtract(formValues.originalTermLength, 'year').isBefore(dayJS(date))
                                        ,
                                        message: (
                                            <FormattedMessage
                                                id="mtg-calculators.Refinance.startDate.errorMessage"
                                                defaultMessage="Please adjust your mortgage term or start date"
                                            />
                                        ),
                                    },
                                    {
                                        fn: (date) =>
                                            // Is the date in the future?
                                            dayJS(date).isBefore(dayJS())
                                        ,
                                        message: (
                                            <FormattedMessage
                                                id="mtg-calculators.Refinance.futureDate.errorMessage"
                                                defaultMessage="Start date must not be in the future"
                                            />
                                        ),
                                    },
                                ],
                            }}
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculators.Refinance.startDate"
                                    defaultMessage="When did your current mortgage start?"
                                />
                            )}
                            isSideBySide={true}
                        />
                    </AnimatedFadeContainer>

                    <CalculatorResultsBox
                        resultValue={(form.values.breakagePenalty
                                        && !form.values.calculatedPenaltyFeeError
                                        || form.values.breakagePenalty === 0)
                            ? form.values.breakagePenalty
                            : undefined
                        }
                        resultMessage={(
                            <FormattedMessage
                                id="mtg-calculators.Refinance.penaltyResultLabel"
                                defaultMessage="The estimated penalty to break your mortgage"
                            />
                        )}
                    />

                    <If condition={form.values.calculatedPenaltyFeeError}>
                        <MessageBanner
                            className="messageBanner"
                            message={(
                                <FormattedMessage
                                    id="mtg-calculators.Refinance.PenaltyStep.tipErrorMessage"
                                    defaultMessage="We are unable to calculate your penalty based on the information provided.
                                        For the most accurate penalty information, please contact your lender directly."
                                />
                            )}
                            iconKey="exclamation-mark"
                            variant="error"
                        />
                    </If>
                </div>
            </CalculatorStepAccordion>
        </Container>
    );
}

function getBreakagePenalty(values) {
    return values.penaltyQuestion ? values.knownPenaltyFee : values.calculatedPenaltyFee;
}

function createProviderOptions(intl) {
    return PENALTY_PROVIDERS.map(provider => (
        {
            label: intl.locale === Locales.ENGLISH
                ? provider.name_en
                : provider.name_fr,
            value: provider.id,
        }
    ));
}

function createYearOptionResolver({ formatMessage }) {
    return years => formatMessage(
        RATE_TERM_MESSAGES.TERM_IN_YEARS_MESSAGE,
        { years },
    );
}

function getFormValuesForBreakagePenalty(values) {
    return {
        principal: values.mortgageBalance,
        rate: values.currentRate,
        type: values.currentRateType,
        term: values.originalTermLength,
        provider: values.existingProvider,
        startDate: values.startDate,
        // comes from RateFilterSelector
        province: values.city?.province.code,
    };
}

function getSavedFormValues(savedCalculatorData) {
    const mortgage = savedCalculatorData?.property?.existingMortgage;
    const term = mortgage?.term ? parseInt(mortgage.term) : undefined;

    return {
        paymentFrequency: mortgage?.paymentFrequencyType,
        mortgagePayment: mortgage?.payment,
        currentRate: mortgage?.interestRate,
        knownPenaltyFee: savedCalculatorData?.refinance?.knownPenaltyFee,
        existingProvider: savedCalculatorData?.provider?.slug,
        currentRateType: mortgage?.type,
        ...term && {
            originalTermLength: term,
            // start date = renewal date - term
            startDate: mortgage?.renewalDate 
                ? dayJS(mortgage.renewalDate).subtract(term, 'year') 
                : undefined,
        },
    };
}

RefinanceStepPenaltyCalculation.propTypes = {
    title: MessagePropType.isRequired,
    ctaText: MessagePropType.isRequired,
    ctaDataName: PropTypes.string.isRequired,
    stepNumber: PropTypes.number.isRequired,

    onConfirmComplete: PropTypes.func,
    onToggleExpand: PropTypes.func,
};

RefinanceStepPenaltyCalculation.defaultProps = {
    onConfirmComplete: undefined,
    onToggleExpand: undefined,
};


const Container = styled.section`
    .stepContent {
        padding: ${Sizes.SPACING.ONE} 6% 0;

        > .messageBanner {
            margin-bottom: ${Sizes.SPACING.TWO};
        }

        .divider {
            display: flex;
            align-items: center;

            margin-bottom: ${Sizes.SPACING.TWO};

            // These pseudo elements are responsible for styling the "hr" on either side of the text
            &::before,
            &::after {
                content: '';
                height: 1px;
                background-color: ${Colours.STONE_DARKEST};
                flex-grow: 1;
                min-width: max-content;
            }

            &::before {
                margin-left: 5%;
                margin-right: 2%;
            }

            &::after {
                margin-left: 2%;
                margin-right: 5%;
            }

            .calculate-penalty {
                text-align: center;
                max-width: 70%;
            }
        }
    }
`;

export default observer(RefinanceStepPenaltyCalculation);
